"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { devLabelPanel } from "@/lib/dev-labels";
import styles from "./HelpModal.module.css";

/**
 * HelpModal — Phase 25-AB Task 5 (panel 1-J / oc-help-modal).
 *
 * Mockup source: docs/mockups/index.html lines 1027-1091.
 *
 * 4 tabs: 단축키 / 새 기능 / 사용 팁 / FAQ.
 * Closes on Esc or backdrop click. Activated by `?` key (wired in EditorHeader).
 */

export type HelpTab = "shortcuts" | "whats-new" | "tips" | "faq";

const TAB_LABELS: Record<HelpTab, string> = {
	shortcuts: "단축키",
	"whats-new": "새 기능",
	tips: "사용 팁",
	faq: "FAQ",
};

const TAB_ORDER: HelpTab[] = ["shortcuts", "whats-new", "tips", "faq"];

export interface HelpModalProps {
	open: boolean;
	onClose: () => void;
	initialTab?: HelpTab;
}

export function HelpModal({ open, onClose, initialTab = "shortcuts" }: HelpModalProps) {
	const [tab, setTab] = useState<HelpTab>(initialTab);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (open) setTab(initialTab);
	}, [open, initialTab]);

	useEffect(() => {
		if (!open) return;
		function onEsc(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		document.addEventListener("keydown", onEsc);
		return () => document.removeEventListener("keydown", onEsc);
	}, [open, onClose]);

	if (!open || !mounted) return null;

	return createPortal(
		<div
			id="oc-help-modal"
			className={`${styles.backdrop} show`}
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
			role="presentation"
		>
			<div
				className={styles.card}
				role="dialog"
				aria-modal="true"
				aria-label="도움말"
				{...devLabelPanel("1-J · HelpModal")}
			>
				<button
					type="button"
					className={styles.close}
					aria-label="닫기"
					onClick={onClose}
				>
					×
				</button>
				<h2 className={styles.heading}>
					<HelpCircleIcon />
					<span>도움말 · 단축키 · 가이드</span>
				</h2>
				<nav className={styles.tabs} role="tablist">
					{TAB_ORDER.map((t) => (
						<button
							key={t}
							type="button"
							role="tab"
							aria-selected={tab === t}
							className={`${styles.tab} ${tab === t ? styles.tabOn : ""}`}
							onClick={() => setTab(t)}
						>
							{TAB_LABELS[t]}
						</button>
					))}
				</nav>
				<div role="tabpanel">
					{tab === "shortcuts" && <ShortcutsTab />}
					{tab === "whats-new" && <WhatsNewTab />}
					{tab === "tips" && <TipsTab />}
					{tab === "faq" && <FAQTab />}
				</div>
				<div className={styles.footer}>
					OpenCut 기반 · CapCut 표준 단축키 적용 · ? 키로 다시 열기
				</div>
			</div>
		</div>,
		document.body,
	);
}

/* ----------------------------------------------------------------- */
/* Tab content — verbatim text from mockup lines 1054-1088.         */
/* ----------------------------------------------------------------- */

interface ShortcutGroup {
	title: string;
	rows: Array<{ label: string; keys: string[] }>;
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
	{
		title: "재생 · 편집 (CapCut/OpenCut 표준)",
		rows: [
			{ label: "재생 / 일시정지", keys: ["Space"] },
			{ label: "역재생 / 정지 / 재생", keys: ["J", "K", "L"] },
			{ label: "In점 / Out점", keys: ["I", "O"] },
			{ label: "Split at playhead", keys: ["C"] },
			{ label: "Split left / Split right", keys: ["Q", "W"] },
			{ label: "Blade 도구", keys: ["B"] },
			{ label: "Selection 도구", keys: ["V"] },
			{ label: "Ripple edit toggle", keys: ["R"] },
			{ label: "Auto snap toggle", keys: ["N"] },
			{ label: "Graph editor", keys: ["G"] },
			{ label: "Bookmark 추가", keys: ["M"] },
			{ label: "Scenes view (drawer)", keys: ["Ctrl", "/"] },
		],
	},
	{
		title: "파일 · 시스템",
		rows: [
			{ label: "저장 / 다른 이름으로", keys: ["Ctrl", "S"] },
			{ label: "Undo / Redo", keys: ["Ctrl", "Z", "Y"] },
			{ label: "복사 / 붙여넣기 / 복제", keys: ["Ctrl", "C", "V", "D"] },
			{ label: "내보내기", keys: ["Ctrl", "E"] },
			{ label: "이름 변경", keys: ["F2"] },
			{ label: "전체화면 (Preview / Editor)", keys: ["F", "F11"] },
			{ label: "1프레임 / 10프레임 이동", keys: ["←/→", "Shift+←/→"] },
			{ label: "줌 in / out", keys: ["+", "-"] },
		],
	},
	{
		title: "우리 추가 (makemonlab Studio)",
		rows: [
			{ label: "도움말 (이 화면)", keys: ["?"] },
			{ label: "명령어 검색 (Cmd-K)", keys: ["Ctrl", "K"] },
			{ label: "AI Magic Wand (전체 자동)", keys: ["Ctrl", "Shift", "A"] },
			{ label: "나레이션 패널", keys: ["Ctrl", "Shift", "N"] },
			{ label: "모자이크 / BGM 빠른 추가", keys: ["Ctrl", "Shift", "M / B"] },
			{ label: "개발 라벨 토글 (이 mockup)", keys: ["D"] },
			{ label: "우측 패널 빠른 이동", keys: ["Ctrl", "1~9"] },
		],
	},
];

function ShortcutsTab() {
	return (
		<>
			{SHORTCUT_GROUPS.map((g) => (
				<div key={g.title} className={styles.section}>
					<div className={styles.sectionTitle}>{g.title}</div>
					{g.rows.map((r) => (
						<div key={r.label} className={styles.row}>
							<span>{r.label}</span>
							<span className={styles.keys}>
								{r.keys.map((k, i) => (
									<span key={`${r.label}-${i}`} className={styles.kbd}>
										{k}
									</span>
								))}
							</span>
						</div>
					))}
				</div>
			))}
		</>
	);
}

function WhatsNewTab() {
	return (
		<div className={styles.section}>
			<div className={styles.sectionTitle}>Phase 25-AB · 진행 중</div>
			<ul className={styles.list}>
				<li>OpenCut fork → makemonlab Studio 변환 작업 중</li>
				<li>Task 5 — Editor Header + 5 sub-components</li>
				<li>Bento card 8-panel 레이아웃 (mockup 1:1)</li>
			</ul>
		</div>
	);
}

function TipsTab() {
	return (
		<div className={styles.section}>
			<div className={styles.sectionTitle}>사용 팁</div>
			<ul className={styles.list}>
				<li>좌측 패널 폭은 드래그로 조절 가능</li>
				<li>Ctrl+K로 명령어 검색 (Cmd-K)</li>
				<li>? 키로 이 도움말을 언제든 다시 열 수 있습니다</li>
				<li>D 키로 개발용 패널 라벨 ON/OFF</li>
			</ul>
		</div>
	);
}

function FAQTab() {
	return (
		<div className={styles.section}>
			<div className={styles.sectionTitle}>FAQ</div>
			<ul className={styles.list}>
				<li>크레딧 부족 시: 헤더 우측 별 아이콘 충전 버튼</li>
				<li>프로젝트 자동 저장 주기: 60초 (헤더 좌측 표시)</li>
				<li>전체화면 종료: F11 또는 Esc</li>
			</ul>
		</div>
	);
}

function HelpCircleIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			style={{ width: "18px", height: "18px", color: "#d4af37" }}
			aria-hidden
		>
			<circle cx="12" cy="12" r="10" />
			<path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
			<line x1="12" y1="17" x2="12.01" y2="17" />
		</svg>
	);
}
